import {ParamComponent} from 'panel';
import pick from 'lodash/pick';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {PropertyFilterFactory} from 'common/widgets/property-filter-menu/models/utils';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {Segmentation} from 'common/types/reports/query';
import {DisplayContext, StringOperator} from 'common/widgets/property-filter-menu/models/types';
import {EntrySectionUpdateEvent, QueryFilterEntry} from 'common/query-builder/query-entry-section/types';

import {StringBucket} from '../types';
import {template} from './template';
import css from './index.inline.styl';

interface StringBucketPaneParams {
  eventNames: string[];
  onUpdate: ({segment, isValid, focusSubmit}: {segment: Segmentation; isValid: boolean; focusSubmit: boolean}) => void;
  segment: Segmentation;
  listProperty?: Segmentation;
}

interface StringBucketPaneJsxParams {
  params: StringBucketPaneParams;
}

export class StringBucketPane extends ParamComponent<StringBucketPaneParams> {
  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      params: {
        eventNames: Array,
        onUpdate: Function,
        segment: Object,
        listProperty: Object,
      },
    };
  }

  createFilter(attrs: Partial<PropertyFilter> = null): PropertyFilter {
    return PropertyFilterFactory.createFromSegment({...attrs}, {displayContext: DisplayContext.Default});
  }

  createTemplateFilter(segment: Segmentation): PropertyFilter {
    const filter = this.createFilter(segment);
    filter.filterValue = [];
    return filter;
  }

  getFilters() {
    return (this.params.segment.customGroups || []).map((prop) => this.createFilter({...this.params.segment, ...prop}));
  }

  getFilterOperatorOverrides(index) {
    const isLabel = index > 0 ? {label: `Else Is`} : {};
    const containsLabel = index > 0 ? {label: `Else Contains`} : {};
    return [
      {value: StringOperator.Equals, ...isLabel},
      {value: StringOperator.Contains, ...containsLabel},
    ];
  }

  handleFiltersUpdate(ev: EntrySectionUpdateEvent<QueryFilterEntry>) {
    const filters = ev.detail.entries.map(({event: value}) => value as PropertyFilter);
    const validFilters = filters
      .filter((filter) =>
        PropertyFilterFactory.createPropertyFilter(filter, {
          displayContext: DisplayContext.Default,
        }).isValid(),
      )
      .map((filter) => pick(filter, [`filterOperator`, `filterValue`, `renamed`]));

    // Copy over any old renames if we are modifying (can skip if adding/deleting)
    // TODO might want the prop menu itself to preserve the renaming
    if (this.params.segment.customGroups?.length === validFilters.length) {
      this.params.segment.customGroups?.forEach((segment, idx) => {
        if (validFilters[idx].renamed === undefined) {
          validFilters[idx].renamed = segment.renamed;
        }
      });
    }

    this.params.onUpdate({
      segment: {
        ...this.params.segment,
        customGroups: validFilters.length ? (validFilters as StringBucket) : undefined,
      },
      isValid: true,
      focusSubmit: true,
    });
  }
}

export const StringBucketPaneEl = defineAndMakeJsxFactory<StringBucketPaneJsxParams>(
  `mp-string-bucket-pane`,
  StringBucketPane,
);
export const StringBucketPaneReact = panel2react<StringBucketPaneJsxParams>(`mp-string-bucket-pane`, StringBucketPane);
